import { useQuery } from '@tanstack/react-query'
import { ConsumptionElement, Object } from '@vae/server/dist/client'
import { ObjectWithElements } from '@vae/server/src/prisma/prismaTypes'
import React, { createContext, useEffect, useMemo, useRef, useState } from 'react'
import { backendCall } from '../../modules/backendApi'
import { trpc } from '../../modules/trpc'
import { PriceHook, useContractPrices } from '../hooks/useContractPrices'
import useUser from '../hooks/useUser'

export interface ObjectContextType extends Omit<ObjectWithElements, 'consumptionElements'> {
	isFilled: {
		baseOptions: boolean
		preferences: boolean
		advancedOptions: boolean
		priceOptions: boolean
	}
	activeConsumptionElements: ConsumptionElement[]
	consumptionElements: ConsumptionElement[]
	hasElectricHeating: boolean
	objectOptions: Object[]

	price: PriceHook
	loading: boolean
	changeCheckDone: boolean
	changeCheckLoading: boolean
	objectOptionsFetching: boolean
	changeCheckFetching: boolean
	hasObjects: boolean
}

export const ObjectContext = createContext({} as ObjectContextType)

const initialObject: ObjectWithElements = {
	id: 0,
	heatingType: null,
	squares: 0,
	type: null,
	heatPump: false,
	underfloorHeating: false,
	fireplace: false,
	temperature: 0,
	moneySaved: 0,
	consumptionElements: [],
	rooms: [],
	useDatahub: false,
	showMonthlyPrice: true,
	brainNet: null,
	chargingLocationId: null,
	city: null,
	contractType: null,
	dhAuthEndDate: null,
	disabledOn: null,
	fixedPrice: null,
	houseNumber: null,
	gsrn: null,
	productionGsrn: null,
	monthlyPrice: null,
	contractEndDate: null,
	contractEndDateType: null,
	personCount: null,
	spotMarginal: null,
	streetAddress: null,
	synerallContractId: null,
	synerallId: null,
	userId: 0,
	zipCode: null,
	synerallProductionId: null,
	panelMaxPower: null,
	hvacAwayMode: false,
	savedTemperature: null,
	electricityTransferMonthlyPrice: null,
	electricityTransferPricePerKwh: null,
	electricityTransferPriceId: null,
	electricityTransferPriceContractDayPrice: null,
	electricityTransferPriceContractNightPrice: null,
	electricityTransferPriceContractType: null,
	manualSetConsumptionProvider: false,
	electricityTaxIncluded: false,
	productionMargin: null,
}

const ObjectContextProvider = (props: any) => {
	const { user, setUser } = useUser()
	const [consumptionElements, setConsumptionElements] = useState<ConsumptionElement[]>([])
	const utils = trpc.useContext()

	const prevObjectId = useRef<number | null>(null)
	const {
		data: objectOptions,
		isLoading: objectOptionsLoading,
		isFetching: objectOptionsFetching,
	} = trpc.user.getObjects.useQuery(
		{ userId: user?.id },
		{
			enabled: Boolean(user?.id),
			staleTime: 1000 * 60 * 60, // 1 hour
		}
	)

	const {
		isSuccess: changeCheckDone,
		isLoading: changeCheckLoading,
		isFetching: changeCheckFetching,
		isError: changeCheckError,
	} = useQuery({
		enabled: Boolean(user?.id) && !objectOptionsLoading,
		queryKey: ['objectChanges', user?.id],
		queryFn: async ({ queryKey }) => backendCall.objects.getChanges(queryKey[1] as number),
		staleTime: 1000 * 60 * 60, // 1 hour
		onSuccess(data: Object[]) {
			if (
				JSON.stringify(objectOptions?.sort((a, b) => a.id! - b.id!)) !==
				JSON.stringify(data.sort((a, b) => a.id! - b.id!))
			) {
				utils.user.getObjects.invalidate()
			}
		},
	})

	const object = useMemo(() => {
		if (!objectOptions || !user) return initialObject

		const selectedObject =
			objectOptions.find(newObject => newObject.id === user.selectedObject) ?? objectOptions[0]

		return selectedObject ?? initialObject
	}, [user?.selectedObject, objectOptions])

	const price = useContractPrices(object)

	useEffect(() => {
		if (!object.id) return

		setConsumptionElements(object.consumptionElements)

		if (prevObjectId.current && prevObjectId.current !== object.id) {
			prevObjectId.current = object.id
			updateDbSelectedObject(object)
		}
	}, [object])

	const hasElectricHeating = useMemo(
		() => object.heatingType?.includes('lectric') ?? false,
		[object]
	)

	const updateDbSelectedObject = (newObject: ObjectWithElements) => {
		if (!user) return

		setUser({
			selectedObject: newObject.id,
			selectedContract: newObject.synerallContractId,
		})
	}

	const baseOptionsFilled = object.heatingType && object.type ? true : false
	const preferencesFilled =
		user?.preference !== null &&
		user?.preference !== undefined &&
		user?.interestedInTech !== null &&
		user?.interestedInTech !== undefined
	const advancedOptionsFilled = object.temperature ? true : false
	const priceOptionsFilled = object.useDatahub
		? object.contractType &&
			(object.fixedPrice || object.spotMarginal) &&
			(object.electricityTransferMonthlyPrice ||
				object.electricityTransferPricePerKwh ||
				object.electricityTransferPriceContractDayPrice ||
				object.electricityTransferPriceContractNightPrice)
			? true
			: false
		: object.electricityTransferMonthlyPrice ||
			  object.electricityTransferPricePerKwh ||
			  object.electricityTransferPriceContractDayPrice ||
			  object.electricityTransferPriceContractNightPrice
			? true
			: false

	const isFilled = {
		baseOptions: baseOptionsFilled,
		preferences: preferencesFilled,
		advancedOptions: advancedOptionsFilled,
		priceOptions: priceOptionsFilled,
	}

	return (
		<ObjectContext.Provider
			value={{
				...object,
				hasObjects: objectOptions ? objectOptions.length > 0 : false,
				objectOptions: objectOptions ?? [],
				isFilled,
				consumptionElements,
				activeConsumptionElements: consumptionElements.filter(element => !element.disabled),
				hasElectricHeating,
				loading: objectOptionsLoading,
				changeCheckDone: changeCheckDone || changeCheckError,
				changeCheckLoading,
				objectOptionsFetching,
				changeCheckFetching,

				price,
			}}
		>
			{props.children}
		</ObjectContext.Provider>
	)
}

export default ObjectContextProvider
